import { apiRequest } from "api-client";
import apiEndpoints from "constants/endpoints";
import { KozoomEvent, KozoomVideo } from "constants/types";
import { capitalize, isNull, isUndefined, omitBy, uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import { useAppSelector } from "store";
import { hmsToSecondsOnly } from 'utils/time';


export const useVideoDetail = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const { languages } = useAppSelector(state => state.lang);

  const defaultVideo: Partial<KozoomVideo> & Partial<{ thumbnailSrcUrl: string, videoSrcUrl: string, uploadedUrl: string, livescoreImportId: string, events: KozoomEvent[] }> = {
    events: [],
    players: [],
    i18n: [],
    showInNewsFeed: false,
  };

  const [video, setVideo] = useState(defaultVideo);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const history = useHistory();

  const location = useLocation<{ video: any, recording: any }>();

  useEffect(() => {
    if (location.state && location.state.video) {
      const liveScoreVideo = location.state.video;
      setTimeout(() => {
        setVideo({
          ...video,
          duration: hmsToSecondsOnly(liveScoreVideo.duration),
          i18n: languages.map((item) => ({ lang: item.iso, description: generateDescription(liveScoreVideo.desc), article: '' })),
          thumbnailSrcUrl: liveScoreVideo.thumbnail,
          videoSrcUrl: liveScoreVideo.stream,
          livescoreImportId: liveScoreVideo.id,
        })
      }, 500)
    }

    // Ant Media import
    if (location.state && location.state.recording) {
      const recording = location.state.recording;
      setTimeout(() => {
        setVideo({
          ...video,
          duration: Math.round(recording.duration / 1000),
          i18n: languages.map((item) => ({ lang: item.iso, description: generateDescription(recording.description), article: '' })),
          videoSrcUrl: recording.url,
          livescoreImportId: recording.id,
        })
      }, 500)
    }
  }, [location]);

  const updateVideo = (field: string, value: any) => {
    if (['events', 'players'].includes(field)) {
      setVideo({
        ...video,
        [field]: uniqBy(value, (item: { id: number }) => item.id)
      });

    } else if (field === 'event') {
      if (value) {
        setVideo({
          ...video,
          eventId: value.id,
          events: uniqBy([value, ...video.events as KozoomEvent[]], (item: KozoomEvent) => item.id)
        });
      } else {
        setVideo({
          ...video,
          eventId: undefined,
        });
      }
    } else {
      setVideo({
        ...video,
        [field]: value
      });
    }
  }

  const onSaveVideo = () => {
    if (!video.i18n) {
      toast.error("You must select a language!");
      return;
    }
    // Default translation to be English version if the content is empty
    const englishTranslation = video.i18n.find(item => item.lang === 'en');
    if (!englishTranslation) {
      toast.error("You must have an English translation!");
      return;
    }
    video.i18n = video.i18n.map(item => ({
      ...item,
      description: item.description ? item.description : englishTranslation.description,
      article: item.article ? item.article : englishTranslation.article,
    }));

    const data = omitBy({
      ...video,
      eventIds: (video.events || []).map(event => event.id),
      playerIds: (video.players || []).map(player => player.id),
      events: undefined,
      players: undefined,
      livescoreImportId: video.livescoreImportId,
      videoSrcUrl: video.videoSrcUrl ? (video.videoSrcUrl.startsWith('http') ? new URL(video.videoSrcUrl).origin + new URL(video.videoSrcUrl).pathname : video.videoSrcUrl) : undefined,
    }, (v: any) => isUndefined(v) || isNull(v));

    if (!data.eventId) {
      toast.error("You must select Main Event!");
    } else if (!data.path && !data.url && !data.bunnyVideoId) {
      toast.error("You must select a video!");
    } else if (videoId) {
      setSaving(true);
      if (videoId === 'new') {
        apiRequest.post(`/${apiEndpoints.videos}`, data, ).then((data) => {
          history.goBack();
          setSaving(false);
          toast.success('Video created successfuly');
        }).catch(err => {
          setSaving(false);
          toast.error(err.message);
        });
      } else {
        // Don't update url
        delete data.url;
        // Thumbnails uploaded manually won't start with http
        if (data.thumbnail?.startsWith('http')) {
          delete data.thumbnail;
        }
        apiRequest.put(`/${apiEndpoints.videos}/${videoId}`, data, ).then((data) => {
          setSaving(false);
          toast.success('Video updated successfuly');
        }).catch(err => {
          setSaving(false);
          toast.error(err.message);
        });
      }
    }
  };

  useEffect(() => {
    if (videoId && videoId !== 'new') {
      setLoading(true);
      apiRequest.get(`${apiEndpoints.videos}/${videoId}`, {
        params: {isAdmin: 1},
      }).then((data) => {
        setVideo(data);
        setLoading(false);
      }).catch(err => {
        toast.error(err.message);
      });
    }
  }, []);

  return {
    videoId,
    loading,
    saving,
    video,
    updateVideo,
    onSaveVideo,
  };
};

const generateDescription = (description: string) => {
  if (!description) {
    return '';
  }
  const parts = description.split(/\[(\d+)\]/);
  if (parts.length === 3) {
    const players = parts[2].split('/');
    if (players.length !== 2) {
      return description;
    }
    const player1Arr = players[0].split(',');
    const player2Arr = players[1].split(',');
    return `${capitalize(player1Arr[1]?.trim())} ${capitalize(player1Arr[0]?.trim())} vs ${capitalize(player2Arr[1]?.trim())} ${capitalize(player2Arr[0]?.trim())} ► ${parts[0].trim()} ► Race to ${parts[1]}`;
  }
  return description;
}

